import React, { useCallback, useEffect } from 'react';

import {
  getIsWorkflowRelativeDateValue,
  WORKFLOW_RELATIVE_DATE_OPERATORS,
  WORKFLOW_RELATIVE_DATE_UNITS,
} from '@aline/common';
import type { WorkflowRelativeDateUnit, WorkflowRelativeDateValue } from '@aline/common';
import { FormControl, Select, Stack, TextField } from '@mui/material';
import type { GridFilterInputValueProps, GridFilterOperator } from '@mui/x-data-grid-premium';
import { dateFilterOperators } from 'src/components/generic/data-grid/date-grid-filter-operators';

const UNIT_LABELS = {
  [WORKFLOW_RELATIVE_DATE_UNITS.day]: 'days',
  [WORKFLOW_RELATIVE_DATE_UNITS.week]: 'weeks',
  [WORKFLOW_RELATIVE_DATE_UNITS.month]: 'months',
} as const;

const DEFAULT_RELATIVE_DATE_VALUE = {
  amount: 1,
  unit: WORKFLOW_RELATIVE_DATE_UNITS.day,
} satisfies WorkflowRelativeDateValue;

function getIsWorkflowRelativeDateUnit(value: string): value is WorkflowRelativeDateUnit {
  return Object.values(WORKFLOW_RELATIVE_DATE_UNITS).some(unit => unit === value);
}

export function RelativeDateFilterInput(
  props: Pick<GridFilterInputValueProps, 'applyValue' | 'item'>
): JSX.Element {
  const { applyValue, item } = props;
  const parsed = getIsWorkflowRelativeDateValue(item.value)
    ? item.value
    : DEFAULT_RELATIVE_DATE_VALUE;

  useEffect(() => {
    if (getIsWorkflowRelativeDateValue(item.value)) return;
    applyValue({ ...item, value: DEFAULT_RELATIVE_DATE_VALUE });
  }, [applyValue, item]);

  const handleAmountChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const amount = Number.parseInt(event.target.value, 10);
      if (!Number.isFinite(amount) || amount < 1) return;
      applyValue({
        ...item,
        value: { amount, unit: parsed.unit } satisfies WorkflowRelativeDateValue,
      });
    },
    [applyValue, item, parsed.unit]
  );

  const handleUnitChange = useCallback(
    (event: { target: { value: string } }) => {
      const unit = event.target.value;
      if (!getIsWorkflowRelativeDateUnit(unit)) return;

      applyValue({
        ...item,
        value: { amount: parsed.amount, unit } satisfies WorkflowRelativeDateValue,
      });
    },
    [applyValue, item, parsed.amount]
  );

  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-end', minWidth: 180 }}>
      <TextField
        size="small"
        slotProps={{
          htmlInput: {
            'aria-label': 'Relative date amount',
            min: 1,
            step: 1,
          },
        }}
        sx={{ width: 56 }}
        type="number"
        value={parsed.amount}
        variant="standard"
        onChange={handleAmountChange}
      />
      <FormControl size="small" variant="standard">
        <Select
          native
          inputProps={{ 'aria-label': 'Relative date unit' }}
          value={parsed.unit}
          onChange={handleUnitChange}
        >
          <option value={WORKFLOW_RELATIVE_DATE_UNITS.day}>
            {UNIT_LABELS[WORKFLOW_RELATIVE_DATE_UNITS.day]}
          </option>
          <option value={WORKFLOW_RELATIVE_DATE_UNITS.week}>
            {UNIT_LABELS[WORKFLOW_RELATIVE_DATE_UNITS.week]}
          </option>
          <option value={WORKFLOW_RELATIVE_DATE_UNITS.month}>
            {UNIT_LABELS[WORKFLOW_RELATIVE_DATE_UNITS.month]}
          </option>
        </Select>
      </FormControl>
    </Stack>
  );
}

function getRelativeDateOperator(options: {
  label: string;
  value: (typeof WORKFLOW_RELATIVE_DATE_OPERATORS)[keyof typeof WORKFLOW_RELATIVE_DATE_OPERATORS];
}): GridFilterOperator {
  return {
    InputComponent: RelativeDateFilterInput,
    getApplyFilterFn: () => null,
    label: options.label,
    value: options.value,
  };
}

const RELATIVE_DATE_OPERATORS: GridFilterOperator[] = [
  getRelativeDateOperator({
    label: 'is in the last',
    value: WORKFLOW_RELATIVE_DATE_OPERATORS.inTheLast,
  }),
  getRelativeDateOperator({
    label: 'is older than',
    value: WORKFLOW_RELATIVE_DATE_OPERATORS.olderThan,
  }),
];

export const workflowDateFilterOperators: GridFilterOperator[] = [
  ...dateFilterOperators,
  ...RELATIVE_DATE_OPERATORS,
];
